import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Loader2, GripVertical, Flag } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";

// Colour ramp shared with ContactStatuses so the palette stays cohesive.
const DEFAULT_COLORS = [
  "#0ea5e9",
  "#f59e0b",
  "#8b5cf6",
  "#059669",
  "#dc2626",
  "#6b7280",
  "#ec4899",
  "#14b8a6",
];

// Slugify a Turkish/English label into a stable stage `key` when the admin
// hasn't provided one. We keep it lowercase, ascii-ish, snake_case.
const slugify = (raw) =>
  (raw || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[çÇ]/g, "c")
    .replace(/[ğĞ]/g, "g")
    .replace(/[ıİ]/g, "i")
    .replace(/[öÖ]/g, "o")
    .replace(/[şŞ]/g, "s")
    .replace(/[üÜ]/g, "u")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

function StageRow({ stage, onSave, onDelete }) {
  const [draft, setDraft] = useState(stage);
  const [busy, setBusy] = useState(false);
  const dirty =
    draft.label !== stage.label ||
    draft.color !== stage.color ||
    draft.order !== stage.order ||
    draft.is_terminal !== stage.is_terminal;

  const doSave = async () => {
    setBusy(true);
    try {
      await onSave(draft);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-200 bg-white p-3"
      data-testid={`platform-stage-row-${stage.id}`}
    >
      <GripVertical className="h-4 w-4 text-zinc-300" />
      <input
        type="color"
        value={draft.color || "#6b7280"}
        onChange={(e) => setDraft({ ...draft, color: e.target.value })}
        className="h-9 w-12 cursor-pointer rounded border border-zinc-200"
        data-testid={`stage-color-${stage.id}`}
      />
      <div className="flex flex-1 flex-col gap-0.5 min-w-[140px]">
        <Input
          value={draft.label}
          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          className="max-w-xs"
          data-testid={`stage-label-${stage.id}`}
        />
        <span className="text-[10px] uppercase tracking-wider text-zinc-400">
          key: {stage.key}
        </span>
      </div>
      <Input
        type="number"
        value={draft.order ?? 0}
        onChange={(e) =>
          setDraft({ ...draft, order: parseInt(e.target.value || "0", 10) })
        }
        className="w-20"
        title="Sıralama"
        data-testid={`stage-order-${stage.id}`}
      />
      <label
        className="flex cursor-pointer items-center gap-2 text-xs text-zinc-500"
        title="Fırsatı kapatan aşama mı? (kazanıldı/kaybedildi gibi)"
      >
        <Switch
          checked={draft.is_terminal === true}
          onCheckedChange={(v) => setDraft({ ...draft, is_terminal: v })}
          data-testid={`stage-terminal-${stage.id}`}
        />
        <span className="inline-flex items-center gap-1">
          <Flag className="h-3 w-3" />
          Kapanış
        </span>
      </label>
      <div className="ml-auto flex items-center gap-1">
        <Button
          size="sm"
          onClick={doSave}
          disabled={!dirty || busy}
          className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
          data-testid={`stage-save-${stage.id}`}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="h-3.5 w-3.5" />
          )}
          Kaydet
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDelete(stage)}
          className="text-red-600 hover:bg-red-50"
          data-testid={`stage-delete-${stage.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function PlatformDealStages() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newKey, setNewKey] = useState("");
  const [newColor, setNewColor] = useState(DEFAULT_COLORS[0]);
  const [newTerminal, setNewTerminal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/deal-stages");
      setRows(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    const label = newLabel.trim();
    const key = (newKey.trim() || slugify(label)).slice(0, 40);
    if (!label || !key) return;
    try {
      const { data } = await api.post("/platform/deal-stages", {
        key,
        label,
        color: newColor,
        order: rows.length + 1,
        is_terminal: newTerminal,
      });
      setRows((cur) => [...cur, data]);
      setCreating(false);
      setNewLabel("");
      setNewKey("");
      setNewColor(DEFAULT_COLORS[0]);
      setNewTerminal(false);
      toast.success("Aşama eklendi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const save = async (draft) => {
    try {
      const { data } = await api.patch(`/platform/deal-stages/${draft.id}`, {
        label: draft.label,
        color: draft.color,
        order: draft.order,
        is_terminal: draft.is_terminal,
      });
      setRows((cur) => cur.map((r) => (r.id === data.id ? data : r)));
      toast.success("Kaydedildi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Silinsin mi: "${row.label}"?`)) return;
    try {
      await api.delete(`/platform/deal-stages/${row.id}`);
      setRows((cur) => cur.filter((r) => r.id !== row.id));
      toast.success("Silindi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="platform-deal-stages-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-950">
            Fırsat Aşamaları
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Satış pipeline&apos;ının kolonlarını burada yönet — sıralama, renk ve
            &quot;kapanış&quot; işareti tüm tenantlarda anında geçerli olur.
          </p>
        </div>
        {!creating && (
          <Button
            onClick={() => setCreating(true)}
            data-testid="add-deal-stage-btn"
            className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
          >
            <Plus className="h-4 w-4" />
            Yeni aşama
          </Button>
        )}
      </div>

      {creating && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-3"
          data-testid="new-stage-form"
        >
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            className="h-9 w-12 cursor-pointer rounded border border-zinc-200"
          />
          <Input
            autoFocus
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Etiket (örn. Sözleşme İmzalandı)"
            className="max-w-xs flex-1 min-w-[180px]"
            data-testid="new-stage-label"
          />
          <Input
            value={newKey}
            onChange={(e) => setNewKey(e.target.value.replace(/[^a-z0-9_\-]/gi, ""))}
            placeholder="key (boşsa otomatik)"
            className="w-40"
            data-testid="new-stage-key"
          />
          <label className="flex items-center gap-2 text-xs text-zinc-500">
            <Switch checked={newTerminal} onCheckedChange={setNewTerminal} data-testid="new-stage-terminal" />
            <span className="inline-flex items-center gap-1">
              <Flag className="h-3 w-3" />
              Kapanış
            </span>
          </label>
          <Button
            size="sm"
            onClick={create}
            className="bg-zinc-900 text-white hover:bg-zinc-800"
            data-testid="new-stage-save"
          >
            Ekle
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setCreating(false)}>
            İptal
          </Button>
        </div>
      )}

      {loading ? (
        <div className="p-6 text-center text-sm text-zinc-500">Yükleniyor…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-8 text-center text-sm text-zinc-500">
          Henüz aşama yok. Sağ üstteki &quot;Yeni aşama&quot; ile ekleyebilirsin.
        </div>
      ) : (
        <div className="space-y-2">
          {rows
            .slice()
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((r) => (
              <StageRow key={r.id} stage={r} onSave={save} onDelete={remove} />
            ))}
        </div>
      )}
    </div>
  );
}
